<!-- Kairos Merit login (design block `isLogin`): the form on cream at the left,
     the brand panel in ink at the right. One shell for the four modes the
     component already handles — login, pedir reset, fijar contraseña y 2FA —
     because they are the same screen with a different question.

     Two things of the design are deliberately NOT here: the "Prototipo · entrar
     como Candidato / Empresa / Admin" buttons, which are prototype navigation
     and would be three ways into the app without credentials, and "Solicitar
     acceso", because there is no public sign-up (the leads come in through the
     web form / GHL and the account is provisioned on approval — see
     AUDITORIA_DISENO §4). Our own `dev login` stays, and only on localhost. -->

<div class="auth">
	<!-- ---- Left: the form -------------------------------------------------- -->
	<section class="auth-panel">
		<div class="auth-box">
			<!-- La marca, centrada y en negro sobre la crema — SOLO en móvil (12/09).
			     Va AQUÍ, fuera de las dos ramas de abajo, porque las tres pantallas
			     —/login, access/candidates y access/companies— son el mismo
			     componente y solo cambian de `audience`: puesta una vez, vale para
			     el formulario y para las cajas de «Inicia sesión / Alta».
			     En pantalla ancha NO sale: la lleva el panel de tinta de la derecha,
			     y verla dos veces es justo por lo que se quitó de aquí. Ese panel se
			     esconde bajo 900 px, y a ese mismo umbral aparece esta. -->
			<div class="auth-lockup">
				<img class="auth-lockup__mark" src="assets/layout/images/branding/kairos-merit-icon.png" alt="" />
				<img
					class="auth-lockup__word"
					src="assets/layout/images/branding/kairos-merit-wordmark-black.png"
					alt="Kairos Merit" />
			</div>
			<!-- Puerta de acceso por audiencia (25/08): antes de preguntar nada, dos
			     cajas — «Inicia sesión» y «Alta». Ocupan el sitio del formulario, no
			     una pantalla previa: quien ya tiene cuenta pulsa una y escribe, sin
			     un paso más de por medio. -->
			@if (choices) {
				<div class="auth-choice">
					<header class="auth-head">
						<h1 class="auth-title">{{ choicesTitleKey | transloco }}</h1>
						<p class="auth-subtitle">{{ choicesSubtitleKey | transloco }}</p>
					</header>
					<div class="auth-choice__list">
						@for (choice of choices; track choice.key) {
							<button type="button" class="auth-choice__card" (click)="choiceSelect.emit(choice.key)">
								<span class="auth-choice__icon">
									<lucide-icon [name]="choice.icon" [size]="20" aria-hidden="true"></lucide-icon>
								</span>
								<span class="auth-choice__body">
									<span class="auth-choice__title">{{ choice.titleKey | transloco }}</span>
									<span class="auth-choice__desc">{{ choice.descriptionKey | transloco }}</span>
								</span>
								<span class="auth-choice__cta">
									{{ choice.ctaKey | transloco }}
									<lucide-icon name="ArrowRight" [size]="15" aria-hidden="true"></lucide-icon>
								</span>
							</button>
						}
					</div>
				</div>
			} @else if (!showSuccessState) {
				<header class="auth-head">
					<h1 class="auth-title">
						@if (isRegister === 1) {
							{{ 'auth.login.title' | transloco }}
						} @else if (isRegister === 2) {
							{{ 'auth.resetPassword.title' | transloco }}
						} @else if (isRegister === 3) {
							{{ 'auth.resetPasswordConfirm.title' | transloco }}
						} @else if (isRegister === 4) {
							{{ 'auth.login.mfa.title' | transloco }}
						} @else if (isRegister === 5) {
							{{ 'auth.activateAccount.title' | transloco }}
						} @else if (isRegister === 6) {
							{{ 'auth.activateAccount.expired.title' | transloco }}
						}
					</h1>
					<p class="auth-subtitle">
						@if (isRegister === 1) {
							{{ 'auth.login.subtitle' | transloco }}
						} @else if (isRegister === 2) {
							{{ 'auth.resetPassword.description' | transloco }}
						} @else if (isRegister === 3) {
							{{ 'auth.resetPasswordConfirm.subtitle' | transloco }}
						} @else if (isRegister === 4) {
							{{ 'auth.login.mfa.description' | transloco }}
						} @else if (isRegister === 5) {
							{{ 'auth.activateAccount.subtitle' | transloco }}
						} @else if (isRegister === 6) {
							{{ 'auth.activateAccount.expired.subtitle' | transloco }}
						}
					</p>
				</header>

				@if (isRegister !== 4) {
					<!-- Native fields on purpose. app-input / app-password carry the
					     backoffice skin (60px, rounded, shadowed) and their own styles
					     out-rank anything scoped here, so reusing them meant a fight
					     over every rule and a password field that overlapped its own
					     link. The reactive form is untouched: `formControlName` binds
					     the same controls and `getErrorMessage()` still writes the
					     errors. -->
					<form class="auth-fields" [formGroup]="authForm" (ngSubmit)="onSubmit()" (keydown.enter)="submitOnEnterSafe($event)">
						@for (field of formFields; track field) {
							@if (field === 'email') {
								<div class="field">
									<label class="field-label" for="auth-email">{{ 'auth.fields.email' | transloco }}</label>
									<input
										id="auth-email"
										type="email"
										class="field-input"
										autocomplete="email"
										formControlName="email"
										[class.is-invalid]="authForm.get('email')?.invalid && authForm.get('email')?.touched"
										[placeholder]="'auth.placeholders.email' | transloco" />
									@if (authForm.get('email')?.invalid && authForm.get('email')?.touched) {
										<span class="field-error">{{ getErrorMessage('email') }}</span>
									}
								</div>
							}

							@if (isRegister !== 2 && field === 'password') {
								<div class="field">
									<label class="field-label" for="auth-password">{{ 'auth.fields.password' | transloco }}</label>
									<!-- «Ojito» (A-P3-03): ver lo tecleado antes de enviar. -->
									<div class="field-password">
										<input
											id="auth-password"
											[type]="shown('password') ? 'text' : 'password'"
											class="field-input"
											autocomplete="current-password"
											formControlName="password"
											[class.is-invalid]="authForm.get('password')?.invalid && authForm.get('password')?.touched"
											[placeholder]="'auth.placeholders.password' | transloco" />
										<button
											type="button"
											class="field-eye"
											[attr.aria-label]="'auth.fields.togglePassword' | transloco"
											[attr.aria-pressed]="shown('password')"
											(click)="toggleShown('password')">
											<lucide-icon [name]="shown('password') ? 'EyeOff' : 'Eye'" [size]="16" aria-hidden="true"></lucide-icon>
										</button>
									</div>
									@if (authForm.get('password')?.invalid && authForm.get('password')?.touched) {
										<span class="field-error">{{ getErrorMessage('password') }}</span>
									}
									@if (isRegister === 1) {
										<!-- Vuelve a estar visible (11/08): el flujo ya existe de punta a
										     punta — en escritorio lleva a `/reset-password` y en móvil el
										     modal llama al mismo endpoint. -->
										<button type="button" class="auth-link" (click)="onForgotPasswordClick()">
											{{ 'auth.login.forgotPassword' | transloco }}
										</button>
										<!-- Legal notice: a quiet line under the password field, opened on
										     demand instead of a wall of text nobody reads before signing in. -->
										<a class="legal-link" href="/legal" target="_blank" rel="noopener">
											<lucide-icon name="Info" [size]="13" aria-hidden="true"></lucide-icon>
											{{ 'auth.legal.link' | transloco }}
										</a>
									}
								</div>
							}

							@if (field === 'newPassword') {
								<div class="field">
									<label class="field-label" for="auth-new-password">
										{{ 'auth.changePassword.newPassword' | transloco }}
									</label>
									<div class="field-password">
										<input
											id="auth-new-password"
											[type]="shown('newPassword') ? 'text' : 'password'"
											class="field-input"
											autocomplete="new-password"
											formControlName="newPassword"
											[class.is-invalid]="authForm.get('newPassword')?.invalid && authForm.get('newPassword')?.touched"
											[placeholder]="'auth.changePassword.newPassword' | transloco" />
										<button
											type="button"
											class="field-eye"
											[attr.aria-label]="'auth.fields.togglePassword' | transloco"
											[attr.aria-pressed]="shown('newPassword')"
											(click)="toggleShown('newPassword')">
											<lucide-icon [name]="shown('newPassword') ? 'EyeOff' : 'Eye'" [size]="16" aria-hidden="true"></lucide-icon>
										</button>
									</div>
									@if (authForm.get('newPassword')?.invalid && authForm.get('newPassword')?.touched) {
										<span class="field-error">{{ getErrorMessage('newPassword') }}</span>
									} @else {
										<span class="field-hint">{{ 'auth.changePassword.hint' | transloco }}</span>
									}
								</div>
							}

							@if ((isRegister === 3 || isRegister === 5) && field === 'confirmPassword') {
								<div class="field">
									<label class="field-label" for="auth-confirm-password">
										{{ 'auth.changePassword.confirmPassword' | transloco }}
									</label>
									<div class="field-password">
										<input
											id="auth-confirm-password"
											[type]="shown('confirmPassword') ? 'text' : 'password'"
											class="field-input"
											autocomplete="new-password"
											formControlName="confirmPassword"
											[class.is-invalid]="authForm.get('confirmPassword')?.invalid && authForm.get('confirmPassword')?.touched"
											[placeholder]="'auth.changePassword.confirmPassword' | transloco" />
										<button
											type="button"
											class="field-eye"
											[attr.aria-label]="'auth.fields.togglePassword' | transloco"
											[attr.aria-pressed]="shown('confirmPassword')"
											(click)="toggleShown('confirmPassword')">
											<lucide-icon [name]="shown('confirmPassword') ? 'EyeOff' : 'Eye'" [size]="16" aria-hidden="true"></lucide-icon>
										</button>
									</div>
									@if (authForm.get('confirmPassword')?.invalid && authForm.get('confirmPassword')?.touched) {
										<span class="field-error">{{ getErrorMessage('confirmPassword') }}</span>
									}
								</div>
							}
						}

						@if (isRegister === 5 && requirePrivacy) {
							<!-- PDPL (10/09): a migrated candidate accepts the privacy policy here,
							     before choosing a password. The backend enforces it; this draws it. -->
							<div class="privacy-consent">
								<label class="trust-device">
									<input type="checkbox" [(ngModel)]="privacyAccepted" [ngModelOptions]="{ standalone: true }" />
									<span>{{ 'auth.activateAccount.privacy.label' | transloco }}</span>
								</label>
								<a class="legal-link" href="/legal" target="_blank" rel="noopener">
									<lucide-icon name="Info" [size]="13" aria-hidden="true"></lucide-icon>
									{{ 'auth.legal.link' | transloco }}
								</a>
								@if (privacyTouched && !privacyAccepted) {
									<span class="field-error">{{ 'auth.activateAccount.privacy.required' | transloco }}</span>
								}
							</div>
						}

						@if (errorMessage) {
							<p class="auth-error" role="alert">{{ errorMessage }}</p>
						}

						<button type="submit" class="auth-submit" [disabled]="loading">
							@if (loading) {
								<span class="auth-spinner" aria-hidden="true"></span>
							}
							{{
								(isRegister === 1
									? 'auth.login.button'
									: isRegister === 2
										? 'auth.resetPassword.button'
										: isRegister === 5
											? 'auth.activateAccount.button'
											: isRegister === 6
												? 'auth.activateAccount.expired.button'
												: 'auth.changePassword.button') | transloco
							}}
						</button>

						<!-- Our own shortcut, localhost only (see `showDevLogin`). Not the
						     design's three role buttons: this one still goes through a real
						     login with real credentials. -->
						@if (showDevLogin) {
							<div class="dev-login">
								<button type="button" class="dev-login__toggle" [disabled]="loading" (click)="toggleDevLoginMenu()">
									{{ 'auth.devLogin.button' | transloco }}
								</button>

								<!-- Opens upwards and floats: it is a local shortcut, and pushing
								     the whole form down every time it opens is not worth it. -->
								@if (devLoginMenuOpen) {
									<div class="dev-login__menu">
										@for (user of devLoginUsers; track user.email) {
											<button type="button" class="dev-login__user" [disabled]="loading" (click)="onDevLogin(user)">
												<span class="dev-login__name">{{ user.label }}</span>
												<span class="dev-login__meta">{{ user.role || user.email }}</span>
											</button>
										} @empty {
											<span class="dev-login__empty">{{ 'auth.devLogin.empty' | transloco }}</span>
										}
									</div>
								}
							</div>
						}
					</form>
				} @else {
					<!-- 2FA. Same shell: it is the second half of the same login, not
					     another screen. -->
					<form class="auth-fields" (ngSubmit)="onMfaSubmit()" (keydown.enter)="$event.preventDefault(); onMfaSubmit()">
						<div class="mfa-boxes">
							@for (digit of mfaCode; track $index) {
								<input
									class="mfa-box"
									type="text"
									inputmode="numeric"
									pattern="[0-9]"
									maxlength="1"
									[id]="'mfa-' + $index"
									[value]="digit"
									[attr.aria-label]="'auth.login.mfa.digit' | transloco: { index: $index + 1 }"
									(input)="onMfaInput($event, $index)"
									(keydown)="onMfaKeydown($event, $index)"
									(focus)="onMfaFocus($event)"
									autocomplete="off" />
							}
						</div>

						@if (errorMessage) {
							<p class="auth-error" role="alert">{{ errorMessage }}</p>
						}

						<label class="trust-device">
							<input type="checkbox" [(ngModel)]="trustDevice" [ngModelOptions]="{ standalone: true }" />
							<span>{{ 'auth.login.mfa.trustDevice' | transloco }}</span>
						</label>

						<button type="submit" class="auth-submit" [disabled]="loading">
							@if (loading) {
								<span class="auth-spinner" aria-hidden="true"></span>
							}
							{{ 'auth.login.mfa.verify' | transloco }}
						</button>
					</form>
				}

				@if (isRegister === 2 || isRegister === 3 || isRegister === 6) {
					<button type="button" class="auth-back" (click)="navigateToLogin()">
						{{
							(isRegister === 3 ? 'auth.resetPasswordConfirm.backToLogin' : 'auth.resetPassword.backToLogin') | transloco
						}}
					</button>
				}
				@if (isRegister === 4) {
					<button type="button" class="auth-back" (click)="onCancelMFA()">
						{{ 'common.actions.cancel' | transloco }}
					</button>
				}
				<!-- Vuelta a las dos cajas. Solo existe si el contenedor la ofrece: en
				     `/login` no hay ningún sitio anterior al que volver. -->
				@if (backLabelKey && isRegister === 1) {
					<button type="button" class="auth-back" (click)="backClick.emit()">
						{{ backLabelKey | transloco }}
					</button>
				}
			} @else {
				<!-- Contraseña cambiada (3) · nuevo enlace enviado (6) -->
				<div class="auth-success">
					<span class="auth-success__icon"><lucide-icon name="CircleCheck" [size]="26"></lucide-icon></span>
					<h1 class="auth-title">{{ successKey('title') | transloco }}</h1>
					<p class="auth-subtitle">{{ successKey('message') | transloco }}</p>
					<button type="button" class="auth-submit" (click)="navigateToLogin()">
						{{ successKey('button') | transloco }}
					</button>
				</div>
			}
		</div>
	</section>

	<!-- ---- Right: the brand ------------------------------------------------ -->
	<aside class="auth-brand" aria-hidden="true">
		<div class="auth-brand__body">
			<img class="auth-brand__mark" src="assets/layout/images/branding/kairos-merit-icon.png" alt="" />
			<!-- Inverted to white, same as the sidebar: there is no white PNG of the
			     wordmark and inverting keeps its transparency. -->
			<img class="auth-brand__word" src="assets/layout/images/branding/kairos-merit-wordmark-black.png" alt="" />
			<p class="auth-brand__claim">{{ 'auth.claim' | transloco }}</p>
			<p class="auth-brand__tags">
				<span>{{ 'auth.tags.place' | transloco }}</span>
				<span class="auth-brand__dot"></span>
				<span>{{ 'auth.tags.sector' | transloco }}</span>
			</p>
		</div>
	</aside>
</div>

<app-forgot-password-modal [(show)]="showForgotPasswordModal" (submit)="onForgotPasswordSubmit($event)">
</app-forgot-password-modal>

